import {
  FileInput,
  Route,
  RouteService,
  Search,
  TFile,
  Trace,
  UserService,
  gpxErrors,
  useCancellablePromise,
  useGeoveloMap,
  useLayers,
  useRouting,
  useSource,
} from '@geovelo-frontends/commons';
import {
  Box,
  Card,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import { gpx } from '@tmcw/togeojson';
import { Link, navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { LngLatBounds } from 'maplibre-gl';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import Dialog from '../../components/dialog';
import { AppContext } from '../../context';
import useTracker from '../../hooks/tracker';

export type TImportType = 'itinerary' | 'stats';

type TProps = Omit<DialogProps, 'onClose'> & {
  defaultImportType?: TImportType;
  onClose: (imported?: boolean) => void;
};

const mapId = 'import-gpx-map';

function ImportGpxDialog({ defaultImportType, onClose, ...props }: TProps): React.JSX.Element {
  const [importType, setImportType] = useState<TImportType>(defaultImportType || 'itinerary');
  const [file, setFile] = useState<TFile | undefined>();
  const [title, setTitle] = useState('');
  const [datetime, setDatetime] = useState('');
  const [route, setRoute] = useState<Route | null>(null);
  const [trace, setTrace] = useState<Trace | null>(null);
  const [loading, setLoading] = useState(false);
  const {
    user: { routes: userRoutes },
    actions: { setUserRoutes },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const theme = useTheme();
  const { trackEvent } = useTracker();
  const { enqueueSnackbar } = useSnackbar();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const {
    map,
    init: initMap,
    destroy: destroyMap,
  } = useGeoveloMap({
    smallDevice: true,
    baseLayer: 'geovelo',
  });
  const {
    addGeoJSONSource: addGPXSource,
    updateGeoJSONSource: updateGPXSource,
    clearGeoJSONSource: clearGPXSource,
  } = useSource(map, 'imported-gpx');
  const { addLineLayer } = useLayers(map);
  const {
    initialized: routingInitialized,
    init: initRouting,
    updateWayPoints: updateWayPointsOnMap,
    showRoutes: showRoutesOnMap,
    destroy: destroyRouting,
  } = useRouting(theme, map, new Search(), {}, { readOnly: true, hidePopup: true }, false);

  useEffect(() => {
    if (!props.open) {
      setFile(undefined);
      setTitle('');
      setRoute(null);
      setTrace(null);
    }

    return () => {
      cancelPromises();
      setLoading(false);
    };
  }, [props.open]);

  useEffect(() => {
    let active = true;

    async function getDatetime() {
      if (file instanceof File) {
        const text = await file.text();
        const collection = gpx(new DOMParser().parseFromString(text, 'text/xml'));

        const time = collection.features[0]?.properties?.time;
        if (active && time) {
          setDatetime(moment(time).format('yyyy-MM-DDThh:mm:ss'));
          setTitle(file.name.substring(0, file.name.lastIndexOf('.')));
        }
      }
    }

    getDatetime();

    return () => {
      setDatetime('');
      active = false;
    };
  }, [file]);

  useEffect(() => {
    if (route) {
      initMap({
        center: { lng: 0.68484, lat: 47.394144 },
        container: mapId,
        customZoomControls: true,
        baseLayersControl: true,
        zoom: 15,
      });
    } else {
      destroyRouting();
      clearGPXSource();
      destroyMap();
    }
  }, [route]);

  useEffect(() => {
    async function showGPXOnMap() {
      if (file instanceof File) {
        const text = await file.text();
        updateGPXSource(gpx(new DOMParser().parseFromString(text, 'text/xml')));
      }
    }

    if (map) {
      addGPXSource();

      addLineLayer(
        'imported-gpx',
        'imported-gpx',
        { 'line-join': 'round', 'line-cap': 'round' },
        {
          'line-color': blueGrey[300],
          'line-width': 9,
        },
      );

      showGPXOnMap();

      initRouting();
    }
  }, [map]);

  useEffect(() => {
    if (routingInitialized && route) {
      updateWayPointsOnMap([route.wayPoints[0], route.wayPoints[route.wayPoints.length - 1]]);
      showRoutesOnMap([route], 0);
      if (route.bounds) {
        const { north, east, south, west } = route.bounds;
        map?.fitBounds(new LngLatBounds({ lat: south, lng: west }, { lat: north, lng: east }), {
          animate: false,
          padding: 50,
        });
      }
    }
  }, [routingInitialized, route]);

  async function importGPX() {
    if (!(file instanceof File) || !title) return;

    setLoading(true);

    if (importType === 'itinerary') {
      try {
        const result = await cancellablePromise(RouteService.getRouteFromGPX(file, title));
        setRoute(result);
        trackEvent('GPX File Uploaded For Routing');
      } catch (err) {
        if (err === 'TooManyPoints' || err === 'NoMatch' || err === 'NoSegment') {
          trackEvent('Upload Of GPX File For Routing Failed', { error: err });
          enqueueSnackbar(t(gpxErrors[err]), { variant: 'error' });
        } else if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
          trackEvent('Upload Of GPX File For Routing Failed');
          enqueueSnackbar(t('geovelo.gpx_dialog.server_error'), { variant: 'error' });
        }
      }
    } else {
      try {
        const trace = await cancellablePromise(UserService.importGPXTrace(file, title));
        trackEvent('GPX File Uploaded For Stats');
        setTrace(trace);
      } catch (err) {
        if (err === 'MissingData' || err === 'AlreadyExists') {
          trackEvent('Upload Of GPX File For Stats Failed', { error: err });
          enqueueSnackbar(t(gpxErrors[err]), { variant: 'error' });
        } else if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
          trackEvent('Upload Of GPX File For Stats Failed');
          enqueueSnackbar(t('geovelo.gpx_dialog.server_error'), { variant: 'error' });
        }
      }
    }

    setLoading(false);
  }

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      onClose={(_, reason) => reason !== 'backdropClick' && onClose()}
      scroll="body"
      {...props}
    >
      {!trace && <DialogTitle>{t('geovelo.gpx_dialog.title')}</DialogTitle>}
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {trace ? (
          <Box alignItems="center" display="flex" flexDirection="column" gap={2}>
            <StaticImage alt="" height={130} src="../../assets/images/success.svg" />
            <Typography align="center" fontSize="1.25em" fontWeight={700}>
              {t('geovelo.gpx_dialog.success_title')}
            </Typography>
            <Typography align="center">{t('geovelo.gpx_dialog.success_description')}</Typography>
          </Box>
        ) : !route ? (
          <>
            <FileInput disabled={loading} file={file} onChange={setFile} size="small" type="gpx" />
            <TextField
              fullWidth
              required
              disabled={loading}
              label={t('commons.title')}
              margin="none"
              onChange={({ target: { value } }) => setTitle(value)}
              size="small"
              value={title || ''}
              variant="outlined"
            />
            {importType === 'stats' && datetime && (
              <TextField
                disabled
                fullWidth
                label={t('commons.date')}
                margin="none"
                size="small"
                type="datetime-local"
                value={datetime}
                variant="outlined"
              />
            )}
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} width="100%">
              <Card
                elevation={0}
                onClick={() => setImportType('itinerary')}
                sx={({ palette }) => ({
                  alignItems: 'center',
                  border: `1px solid ${importType === 'itinerary' ? palette.primary.main : 'transparent'}`,
                  display: 'flex',
                  width: { xs: '100%', md: 'calc((100% - 16px) / 2)' },
                  padding: 1,
                  cursor: 'pointer',
                  gap: 1,
                })}
              >
                <Radio disableRipple checked={importType === 'itinerary'} disabled={loading} />
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography
                    color={importType === 'itinerary' ? 'primary' : 'textSecondary'}
                    lineHeight={1.2}
                    variant="subtitle1"
                  >
                    {t('geovelo.gpx_dialog.import_type.itinerary_title')}
                  </Typography>
                  <Typography variant="body2">
                    {t('geovelo.gpx_dialog.import_type.itinerary_description')}
                  </Typography>
                </Box>
              </Card>
              <Card
                elevation={0}
                onClick={() => setImportType('stats')}
                sx={({ palette }) => ({
                  alignItems: 'center',
                  border: `1px solid ${importType === 'stats' ? palette.primary.main : 'transparent'}`,
                  display: 'flex',
                  width: { xs: '100%', md: 'calc((100% - 16px) / 2)' },
                  padding: 1,
                  cursor: 'pointer',
                  gap: 1,
                })}
              >
                <Radio checked={importType === 'stats'} />
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box alignItems="center" display="flex" gap={1}>
                    <Typography
                      color={importType === 'stats' ? 'primary' : 'textSecondary'}
                      lineHeight={1.2}
                      variant="subtitle1"
                    >
                      {t('geovelo.gpx_dialog.import_type.stats_title')}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {t('geovelo.gpx_dialog.import_type.stats_description')}
                  </Typography>
                </Box>
              </Card>
            </Box>
          </>
        ) : (
          <>
            <Box
              borderRadius={2}
              height={300}
              id={mapId}
              margin="16px 0 16px"
              position="relative"
            />
            <Typography>{t('geovelo.gpx_dialog.adapted_route')}</Typography>
            <Typography>{t('geovelo.gpx_dialog.save_route')}</Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        {trace ? (
          <>
            <Button onClick={() => onClose(true)} size="small">
              {t('commons.actions.close')}
            </Button>
            <Button
              color="primary"
              component={Link}
              size="small"
              to={`/${currentLanguage}/user/traces/${trace.id}`}
              variant="contained"
            >
              {t('geovelo.gpx_dialog.actions.show_trace')}
            </Button>
          </>
        ) : !route ? (
          <>
            <Button onClick={() => onClose()} size="small">
              {t('commons.actions.cancel')}
            </Button>
            <Button
              color="primary"
              disabled={!file || !title || loading}
              onClick={() => importGPX()}
              startIcon={loading && <CircularProgress color="inherit" size={16} thickness={4} />}
              variant="contained"
            >
              {t('geovelo.actions.import')}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                RouteService.remove(route.id);
                onClose();
              }}
              size="small"
            >
              {t('commons.actions.cancel')}
            </Button>
            <Button
              color="primary"
              onClick={() => {
                if (userRoutes) setUserRoutes([route, ...userRoutes]);
                navigate(`/${currentLanguage}/saved-route/${route.id}`);
              }}
              size="small"
              variant="contained"
            >
              {t('commons.actions.confirm')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImportGpxDialog;
